import {Modal, Text, Tag} from '@/components/atoms'
import {SelectableList} from '@/components/molecules'
import {getAllSegments, getSegmentsByGroupId} from '@/services/segments'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {SegmentTypes} from '@/utils/constants/promotionConstants'
import {useEffect, useMemo, useRef, useState} from 'react'
import './segmentModal.scss'

// Create a module-level constant to provide a stable reference for default props
// This avoids creating a new [] on every render which was previously
// triggering useEffect dependency changes and causing repeated API calls.
const DEFAULT_EXCLUDE_SEGMENT_IDS = []

/**
 * SegmentModalContent - Handles segment selection with two different modes:
 *
 * 1. SELECTED_GROUPS: Shows folders and segments side by side
 *    - Uses client-side search (filters already loaded items)
 *    - No pagination (loads all segments for selected group)
 *
 * 2. SELECTED_SEGMENTS: Shows all segments in a single list
 *    - Uses API-based search with debouncing
 *    - Supports pagination for better performance with large datasets
 *    - Maintains selections across page changes
 */
const SegmentModalContent = ({
  type,
  onSelection,
  segmentGroupData,
  existingSelections,
  isExcluded,
  excludeSegmentIds = DEFAULT_EXCLUDE_SEGMENT_IDS,
}) => {
  // Track which groups have been checked (this is what will be saved on Apply)
  const [checkedSegmentGroups, setCheckedSegmentGroups] = useState([])
  // Track which group is currently selected for viewing segments
  const [selectedSegmentGroupId, setSelectedSegmentGroupId] = useState(null)
  const [segmentList, setSegmentList] = useState([])
  const [isLoadingSegments, setIsLoadingSegments] = useState(false)
  const [selectedSegments, setSelectedSegments] = useState([])
  // Store segment selections per group: { groupId: [{ id, name }] }
  const [segmentSelectionsByGroup, setSegmentSelectionsByGroup] = useState({})
  // Store total segment counts per group: { groupId: number }
  const [segmentCountsByGroup, setSegmentCountsByGroup] = useState({})
  // For SELECTED_SEGMENTS type, store all segments
  const [allSegments, setAllSegments] = useState([])
  // Store all selected segment IDs across pages for SELECTED_SEGMENTS type
  const [allSelectedSegmentIds, setAllSelectedSegmentIds] = useState(new Set())
  const selectedSegmentsToPass = useRef([])
  const hasInitializedFromExisting = useRef(false)

  // Pagination state for SELECTED_SEGMENTS
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalSegments, setTotalSegments] = useState(0)
  const pageSize = 10

  // Search state for API-based search (SELECTED_SEGMENTS type only)
  const [apiSearchTerm, setApiSearchTerm] = useState('')
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)

  // Reset states when modal type changes or modal opens
  useEffect(() => {
    setCurrentPage(0)
    setApiSearchTerm('')
    setAllSelectedSegmentIds(new Set())
    hasInitializedFromExisting.current = false // Reset initialization flag
    setSelectedSegments([])
    selectedSegmentsToPass.current = []
    setSegmentSelectionsByGroup({})
    setCheckedSegmentGroups([])

    // Clear any pending search timer
    setSearchDebounceTimer(prev => {
      if (prev) {
        clearTimeout(prev)
      }
      return null
    })
  }, [type])

  // Initialize checked groups from existing selections when modal opens
  useEffect(() => {
    if (hasInitializedFromExisting.current) {
      return
    }

    if (type === SegmentTypes.SELECTED_SEGMENTS && existingSelections) {
      // Check if existing selections match the current type
      const selectionTypeMatches = !existingSelections.selectionType || existingSelections.selectionType === type

      // Handle excluded segments case first (legacy and current)
      if (isExcluded) {
        // Check if it's the new format with selectionType
        if (
          existingSelections &&
          existingSelections.segments &&
          existingSelections.segmentsView &&
          selectionTypeMatches
        ) {
          // New format with selectionType
          const segmentIds = existingSelections.segments || []
          setAllSelectedSegmentIds(new Set(segmentIds))

          // Use segmentsView to get segment details
          const selectedSegmentObjects = (existingSelections.segmentsView || []).map(view => ({
            id: view.segmentId,
            name: view.segmentName,
          }))

          setSelectedSegments(selectedSegmentObjects)
          selectedSegmentObjects?.forEach(obj => {
            if (!selectedSegmentsToPass.current.find(seg => seg.id === obj.id)) {
              selectedSegmentsToPass.current = [...selectedSegmentsToPass.current, obj]
            }
          })
        } else if (Array.isArray(existingSelections)) {
          // Legacy array format
          const segmentIds = existingSelections.map(seg => (typeof seg === 'object' ? seg.id : seg))
          if (segmentIds.length) {
            setAllSelectedSegmentIds(new Set(segmentIds))
          }

          // Initialize selectedSegments with whatever segments we have loaded
          const selectedSegmentObjects = existingSelections.map(segment => {
            if (typeof segment === 'object') {
              return segment
            }
            // For ID format, create placeholder (name will be populated later when segments load)
            return {id: segment, name: `Segment ${segment}`}
          })

          setSelectedSegments(selectedSegmentObjects)
        }
      } else if (!isExcluded && existingSelections && selectionTypeMatches) {
        // Check if existingSelections is a single SelectedSegmentAPIGroup object (backend format)
        if (existingSelections.segments && existingSelections.segmentsView) {
          // Backend format: SelectedSegmentAPIGroup
          const segmentIds = existingSelections.segments || []
          setAllSelectedSegmentIds(new Set(segmentIds))

          // Use segmentsView to get segment details
          const selectedSegmentObjects = (existingSelections.segmentsView || []).map(view => ({
            id: view.segmentId,
            name: view.segmentName,
          }))

          setSelectedSegments(selectedSegmentObjects)
          selectedSegmentObjects?.forEach(obj => {
            if (!selectedSegmentsToPass.current.find(seg => seg.id === obj.id)) {
              selectedSegmentsToPass.current = [...selectedSegmentsToPass.current, obj]
            }
          })
        }
      }
      hasInitializedFromExisting.current = true
    } else if (existingSelections && type === SegmentTypes.SELECTED_GROUPS) {
      // Check if existing selections match the current type
      const selectionTypeMatches = !existingSelections.selectionType || existingSelections.selectionType === type
      // Check if existingSelections is a single SelectedSegmentAPIGroup object (backend format)
      if (existingSelections.segments && existingSelections.segmentsView && selectionTypeMatches) {
        // Backend format: SelectedSegmentAPIGroup - group segments by segmentGroupId
        const groupedSegments = {}

        existingSelections?.segmentsView.forEach(view => {
          const groupId = view.segmentGroupId || 'individual-segments'
          if (!groupedSegments[groupId]) {
            groupedSegments[groupId] = {
              groupId,
              groupName: groupId === 'individual-segments' ? 'Selected Segments' : `Group ${groupId}`,
              segments: [],
            }
          }
          groupedSegments[groupId].segments.push({
            segmentId: view.segmentId,
            segmentName: view.segmentName,
          })
        })

        const groupsArray = Object.values(groupedSegments)

        const initialCheckedGroups = groupsArray.map(group => ({
          id: group.groupId,
          label: group.groupName,
        }))
        setCheckedSegmentGroups(initialCheckedGroups)

        // Also initialize segment selections
        const initialSelections = {}
        groupsArray.forEach(group => {
          initialSelections[group.groupId] = (group.segments || []).map(seg => ({
            id: seg.segmentId,
            name: seg.segmentName,
          }))
        })
        setSegmentSelectionsByGroup(initialSelections)
      } else if (existingSelections.length > 0) {
        // Legacy array format
        const initialCheckedGroups = existingSelections.map(group => ({
          id: group.groupId,
          label: group.groupName,
        }))
        setCheckedSegmentGroups(initialCheckedGroups)

        // Also initialize segment selections
        const initialSelections = {}
        existingSelections.forEach(group => {
          initialSelections[group.groupId] = (group.segments || []).map(seg => ({
            id: seg.segmentId,
            name: seg.segmentName,
          }))
        })
        setSegmentSelectionsByGroup(initialSelections)

        // Fetch total counts for existing groups
        existingSelections.forEach(async group => {
          try {
            const response = await getSegmentsByGroupId(group.groupId, 0, 1) // Only need count
            if (response?.totalRecords !== undefined) {
              setSegmentCountsByGroup(prev => ({
                ...prev,
                [group.groupId]: response.totalRecords,
              }))
            }
          } catch (error) {
            console.error('Error fetching segment count for group:', group.groupId, error)
          }
        })
      }
      hasInitializedFromExisting.current = true
    }
  }, [existingSelections, type, isExcluded]) // Removed allSegments to prevent running on every page change

  // Update selectedSegments when allSegments or allSelectedSegmentIds changes for SELECTED_SEGMENTS type
  useEffect(() => {
    if (type === SegmentTypes.SELECTED_SEGMENTS && hasInitializedFromExisting.current) {
      if (allSelectedSegmentIds.size === 0) {
        // Clear selectedSegments when no segments are selected
        setSelectedSegments([])
      } else if (allSegments.length > 0) {
        // Preserve existing names if the segment isn't present in the current page of allSegments
        setSelectedSegments(prevSelectedSegments => {
          const selectedSegmentObjects = Array.from(allSelectedSegmentIds).map(segmentId => {
            const segmentFromPage = allSegments.find(seg => seg.id === segmentId)
            if (segmentFromPage) return segmentFromPage

            // Fallback to previously stored name in selectedSegments (if any)
            const previouslySelected = prevSelectedSegments.find(seg => seg.id === segmentId)
            if (previouslySelected) return previouslySelected

            // Default placeholder (keeps tag visible until name encountered in later pages)
            return {id: segmentId, name: `Segment ${segmentId}`}
          })

          return selectedSegmentObjects
        })
      }
    }
  }, [allSegments, allSelectedSegmentIds, type])

  // Fetch total counts for all groups when modal opens with SELECTED_GROUPS type
  useEffect(() => {
    const fetchAllGroupCounts = async () => {
      if (type === SegmentTypes.SELECTED_GROUPS && segmentGroupData && segmentGroupData.length > 0) {
        // Only fetch counts for groups that have existing selections
        const groupsToFetch = segmentGroupData.filter(group => {
          // Check if this group has existing selections
          return (
            existingSelections &&
            Array.isArray(existingSelections) &&
            existingSelections?.some(selection => selection?.groupId === group.id)
          )
        })

        if (groupsToFetch.length === 0) {
          return // No need to fetch if no existing selections
        }

        // Fetch counts only for groups with existing selections
        const countPromises = groupsToFetch.map(async group => {
          try {
            // Use pageSize=1 to just get the count, not all segments
            const response = await getSegmentsByGroupId(group.id, 0, 1)

            let actualCount = response?.totalRecords || 0

            // Only fetch all segments if we need to filter
            if (!isExcluded && excludeSegmentIds.length > 0 && actualCount > 0) {
              // In this case, we need to fetch all to get accurate filtered count
              const fullResponse = await getSegmentsByGroupId(group.id, 0, 500)
              const segments = fullResponse?.data || []
              const filteredSegments = segments.filter(seg => !excludeSegmentIds.includes(seg.segmentId))
              actualCount = filteredSegments?.length || 0
            }

            return {groupId: group.id, count: actualCount}
          } catch (error) {
            console.error('Error fetching segment count for group:', group.id, error)
            return {groupId: group.id, count: 0}
          }
        })

        const counts = await Promise.all(countPromises)

        // Update all counts at once
        const newCounts = {}
        counts.forEach(({groupId, count}) => {
          newCounts[groupId] = count
        })

        setSegmentCountsByGroup(prev => ({
          ...prev,
          ...newCounts,
        }))
      }
    }

    fetchAllGroupCounts()
  }, [type, segmentGroupData, isExcluded, excludeSegmentIds, existingSelections])

  // Fetch segments with pagination when type is SELECTED_SEGMENTS
  useEffect(() => {
    const fetchPaginatedSegments = async () => {
      if (type === SegmentTypes.SELECTED_SEGMENTS) {
        setIsLoadingSegments(true)
        try {
          // Fetch all segments without group filtering
          const response = await getAllSegments(currentPage, pageSize, apiSearchTerm)

          const segments = response?.data || []
          let formattedSegments = segments.map(({segmentId, segmentName}) => ({
            id: segmentId,
            name: segmentName,
          }))

          // Filter out segments that are already included if this is for exclusion
          if (isExcluded && excludeSegmentIds.length > 0) {
            formattedSegments = formattedSegments.filter(segment => !excludeSegmentIds.includes(segment.id))
          }

          // Always set the segments from the response, even if empty
          setAllSegments(formattedSegments)

          // Set pagination info from response
          if (response?.totalRecords !== undefined) {
            // Adjust total count if we filtered out segments
            const filteredCount =
              isExcluded && excludeSegmentIds.length > 0
                ? response.totalRecords - excludeSegmentIds.length
                : response.totalRecords

            setTotalSegments(Math.max(0, filteredCount))
            setTotalPages(Math.ceil(Math.max(0, filteredCount) / pageSize))
          } else {
            // If no totalRecords, assume we got all results on this page
            setTotalSegments(formattedSegments.length)
            setTotalPages(formattedSegments.length > 0 ? 1 : 0)
          }

          setIsLoadingSegments(false)
        } catch (error) {
          console.error('Error fetching paginated segments:', error)
          // Ensure we clear the segments on error
          setAllSegments([])
          setTotalSegments(0)
          setTotalPages(0)
          setIsLoadingSegments(false)
        }
      }
    }

    fetchPaginatedSegments()
  }, [type, currentPage, pageSize, apiSearchTerm, isExcluded, excludeSegmentIds])

  // Notify parent component about selected segments in the expected format
  useEffect(() => {
    if (type === SegmentTypes.SELECTED_SEGMENTS) {
      const selectionData = {
        segments: selectedSegmentsToPass.current,
        selectedGroups: [],
        segmentsByGroup: {},
      }

      onSelection(selectionData)
    } else {
      // Format the data to match what the parent expects (DataTable format)
      const formattedSelection = selectedSegments.map(segment => ({
        original: segment,
      }))

      // Create an enhanced segmentsByGroup that includes group names
      // Only include segments for groups that are checked
      const enhancedSegmentsByGroup = {}
      Object.entries(segmentSelectionsByGroup).forEach(([groupId, segments]) => {
        // Only include this group's segments if the group is checked
        if (checkedSegmentGroups.some(group => group.id === groupId)) {
          const groupData = segmentGroupData?.find(group => group.id === groupId)
          enhancedSegmentsByGroup[groupId] = {
            groupName: groupData?.name || '',
            segments: segments,
          }
        }
      })

      // Include information about which groups are checked (will be saved on Apply)
      const selectionData = {
        segments: formattedSelection,
        selectedGroups: checkedSegmentGroups,
        segmentsByGroup: enhancedSegmentsByGroup,
      }

      onSelection(selectionData)
    }
  }, [
    selectedSegments,
    checkedSegmentGroups,
    segmentSelectionsByGroup,
    segmentGroupData,
    onSelection,
    type,
    allSelectedSegmentIds,
    allSegments,
  ])

  // Fetch segments when a group is selected
  useEffect(() => {
    const fetchSegments = async () => {
      if (selectedSegmentGroupId && type !== SegmentTypes.SELECTED_SEGMENTS) {
        setIsLoadingSegments(true)
        try {
          const response = await getSegmentsByGroupId(selectedSegmentGroupId, 0, 500) // No pagination for SELECTED_GROUPS
          const segments = response?.data || []
          let formattedSegments = segments.map(({segmentId, segmentName}) => ({
            id: segmentId,
            name: segmentName,
          }))

          // Filter out excluded segments if we're selecting segments to include
          if (!isExcluded && excludeSegmentIds.length > 0) {
            formattedSegments = formattedSegments.filter(segment => !excludeSegmentIds.includes(segment.id))
          }

          setSegmentList(formattedSegments)

          // Store the total count for this group
          if (response?.totalRecords !== undefined) {
            // Adjust count if we filtered segments
            const adjustedCount =
              !isExcluded && excludeSegmentIds.length > 0 ? formattedSegments.length : response.totalRecords

            setSegmentCountsByGroup(prev => ({
              ...prev,
              [selectedSegmentGroupId]: adjustedCount,
            }))
          }

          // Check if we have previous selections for this group
          const previousSelections = segmentSelectionsByGroup[selectedSegmentGroupId]
          if (previousSelections && previousSelections.length > 0) {
            // Filter previous selections to exclude any that are now filtered out
            const validSelections = previousSelections.filter(seg =>
              formattedSegments.some(formattedSeg => formattedSeg.id === seg.id)
            )
            setSelectedSegments(validSelections)

            // Update stored selections if any were filtered out
            if (validSelections.length !== previousSelections.length) {
              setSegmentSelectionsByGroup(prev => ({
                ...prev,
                [selectedSegmentGroupId]: validSelections,
              }))
            }
          } else if (checkedSegmentGroups.some(group => group.id === selectedSegmentGroupId)) {
            // If this group is checked but has no selections yet, select all segments
            setSelectedSegments(formattedSegments)
            setSegmentSelectionsByGroup(prev => ({
              ...prev,
              [selectedSegmentGroupId]: formattedSegments,
            }))
          } else {
            // Group is not checked, show segments unselected
            setSelectedSegments([])
          }
        } catch (error) {
          console.error('Error fetching segments:', error)
          setSegmentList([])
          setSelectedSegments([])
        } finally {
          setIsLoadingSegments(false)
        }
      } else if (type !== SegmentTypes.SELECTED_SEGMENTS) {
        setSegmentList([])
        setSelectedSegments([])
      }
    }

    fetchSegments()
  }, [selectedSegmentGroupId, checkedSegmentGroups, type, isExcluded, excludeSegmentIds, segmentSelectionsByGroup])

  const handleGroupSelect = item => {
    // Only select the group for viewing segments, don't affect checked state
    setSelectedSegmentGroupId(item.id)
  }

  const handleCheckboxChange = async (itemId, checked) => {
    if (checked) {
      // Add the group to checked groups
      setCheckedSegmentGroups(prev => {
        if (!prev.some(group => group.id === itemId)) {
          const groupData = segmentGroupData.find(group => group.id === itemId)
          return [...prev, {id: itemId, label: groupData?.name || ''}]
        }
        return prev
      })

      // Also select this group to show its segments
      setSelectedSegmentGroupId(itemId)

      // Only fetch and select all segments if none are currently selected
      const currentSelections = segmentSelectionsByGroup[itemId]
      if (!currentSelections || currentSelections.length === 0) {
        // Fetch and select all segments for this group
        try {
          const response = await getSegmentsByGroupId(itemId, 0, 500) // No pagination for SELECTED_GROUPS
          const segments = response?.data || []
          let formattedSegments = segments.map(({segmentId, segmentName}) => ({
            id: segmentId,
            name: segmentName,
          }))

          // Filter out excluded segments if we're selecting segments to include
          if (!isExcluded && excludeSegmentIds.length > 0) {
            formattedSegments = formattedSegments.filter(segment => !excludeSegmentIds.includes(segment.id))
          }

          // Store the total count for this group
          if (response?.totalRecords !== undefined) {
            const adjustedCount =
              !isExcluded && excludeSegmentIds.length > 0 ? formattedSegments.length : response.totalRecords

            setSegmentCountsByGroup(prev => ({
              ...prev,
              [itemId]: adjustedCount,
            }))
          }

          // Store all segments as selected for this group
          setSegmentSelectionsByGroup(prev => ({
            ...prev,
            [itemId]: formattedSegments,
          }))

          // If this is the currently viewed group, update the selected segments display
          if (selectedSegmentGroupId === itemId) {
            setSelectedSegments(formattedSegments)
          }
        } catch (error) {
          console.error('Error fetching segments for checkbox:', error)
        }
      }
      // If segments are already selected, keep them as is
    } else {
      // Remove from checked groups
      setCheckedSegmentGroups(prev => prev.filter(group => group.id !== itemId))

      // Remove all segment selections for this group
      setSegmentSelectionsByGroup(prev => {
        const newSelections = {...prev}
        delete newSelections[itemId]
        return newSelections
      })

      // Remove the segment count for this group
      setSegmentCountsByGroup(prev => {
        const newCounts = {...prev}
        delete newCounts[itemId]
        return newCounts
      })

      // If this is the currently viewed group, clear selected segments
      if (selectedSegmentGroupId === itemId) {
        setSelectedSegments([])
      }
    }
  }

  const handleSegmentCheckboxChange = (segmentId, checked) => {
    if (type === SegmentTypes.SELECTED_SEGMENTS) {
      // For SELECTED_SEGMENTS type, maintain selections across pages
      setAllSelectedSegmentIds(prev => {
        const newSet = new Set(prev)
        if (checked) {
          newSet.add(segmentId)
        } else {
          newSet.delete(segmentId)
        }
        return newSet
      })

      // Also update the displayed segments if the segment is on the current page
      setSelectedSegments(prev => {
        if (checked) {
          if (!prev.some(seg => seg.id === segmentId)) {
            const segmentData = allSegments.find(seg => seg.id === segmentId)
            if (segmentData) {
              if (!selectedSegmentsToPass.current?.find(seg => seg.id === segmentId)) {
                selectedSegmentsToPass.current = [...selectedSegmentsToPass.current, segmentData]
              }
              return [...prev, {id: segmentId, name: segmentData.name}]
            }
          }
          return prev
        } else {
          selectedSegmentsToPass.current = selectedSegmentsToPass.current?.filter(seg => seg.id !== segmentId)
          return prev.filter(seg => seg.id !== segmentId)
        }
      })
    } else {
      // Original logic for SELECTED_GROUPS
      setSelectedSegments(prev => {
        let newSelection
        if (checked) {
          if (!prev.some(seg => seg.id === segmentId)) {
            const segmentData = segmentList.find(seg => seg.id === segmentId)
            newSelection = [...prev, {id: segmentId, name: segmentData?.name || ''}]
          } else {
            newSelection = prev
          }
        } else {
          newSelection = prev.filter(seg => seg.id !== segmentId)
        }

        // Also update the stored selections for this group
        if (selectedSegmentGroupId) {
          setSegmentSelectionsByGroup(prevGroups => ({
            ...prevGroups,
            [selectedSegmentGroupId]: newSelection,
          }))

          // Update checkedSegmentGroups based on whether any segments are selected
          if (newSelection.length > 0) {
            // Ensure the group is in checkedSegmentGroups
            setCheckedSegmentGroups(prev => {
              if (!prev.some(group => group.id === selectedSegmentGroupId)) {
                const groupData = segmentGroupData.find(group => group.id === selectedSegmentGroupId)
                return [...prev, {id: selectedSegmentGroupId, label: groupData?.name || ''}]
              }
              return prev
            })
          } else {
            // No segments selected, remove from checkedSegmentGroups
            setCheckedSegmentGroups(prev => prev.filter(group => group.id !== selectedSegmentGroupId))
          }
        }

        return newSelection
      })
    }
  }

  // Remove a selected segment when its corresponding tag's X button is clicked
  const handleTagRemove = segmentId => {
    if (type === SegmentTypes.SELECTED_SEGMENTS) {
      // For paginated view we can reuse checkbox handler
      handleSegmentCheckboxChange(segmentId, false)
    } else {
      // For SELECTED_GROUPS find which group the segment belongs to
      let foundGroupId = null
      Object.entries(segmentSelectionsByGroup).forEach(([groupId, segs]) => {
        if (segs.some(seg => seg.id === segmentId)) {
          foundGroupId = groupId
        }
      })

      if (foundGroupId) {
        // Remove segment from that group's selection list
        setSegmentSelectionsByGroup(prev => {
          const updated = {...prev}
          const updatedSegs = (updated[foundGroupId] || []).filter(seg => seg.id !== segmentId)
          if (updatedSegs.length > 0) {
            updated[foundGroupId] = updatedSegs
          } else {
            delete updated[foundGroupId]
          }
          return updated
        })

        // Update checked groups based on whether the group still has selections
        setCheckedSegmentGroups(prev => {
          const groupStillHasSelections =
            prev.find(g => g.id === foundGroupId) && (segmentSelectionsByGroup[foundGroupId]?.length || 0) > 1 // because we are about to remove one

          if (groupStillHasSelections) {
            return prev
          }
          return prev.filter(g => g.id !== foundGroupId)
        })

        // If current viewing group is the same, update displayed selections
        if (selectedSegmentGroupId === foundGroupId) {
          setSelectedSegments(prev => prev.filter(seg => seg.id !== segmentId))
        }
      }
    }
  }

  const handlePageChange = newPage => {
    setCurrentPage(newPage)
  }

  // Handle search for SELECTED_SEGMENTS type (API-based search with debouncing)
  const handleApiSearch = searchValue => {
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }

    // Set a new timer to debounce the API call
    const newTimer = setTimeout(() => {
      setApiSearchTerm(searchValue)
      setCurrentPage(0) // Reset to first page when searching
    }, 300) // 300ms debounce delay

    setSearchDebounceTimer(newTimer)
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
    }
  }, [searchDebounceTimer])

  // Transform data for SelectableList
  const transformedSegmentGroups = useMemo(() => {
    return (
      segmentGroupData?.map(({id, name, segments}) => {
        const selectedCount = segmentSelectionsByGroup[id]?.length || 0
        // Use the stored count if available, otherwise fall back to the segments array length
        const totalCount = segmentCountsByGroup[id] || (Array.isArray(segments) ? segments.length : 0)

        // Check if either:
        // 1. The group is explicitly checked
        // 2. There are any segments selected in this group
        const isChecked = checkedSegmentGroups.some(group => group.id === id) || selectedCount > 0

        return {
          id,
          label: name,
          subLabel: selectedCount > 0 ? `${selectedCount}/${totalCount} Selected` : undefined,
          checked: isChecked,
          selected: selectedSegmentGroupId === id, // Only one row can be visually selected at a time
        }
      }) || []
    )
  }, [segmentGroupData, checkedSegmentGroups, selectedSegmentGroupId, segmentSelectionsByGroup, segmentCountsByGroup])

  // Transform segments for SelectableList
  const transformedSegments = useMemo(() => {
    const segmentsToTransform = type === SegmentTypes.SELECTED_SEGMENTS ? allSegments : segmentList

    return segmentsToTransform.map(({id, name}) => {
      const isChecked =
        type === SegmentTypes.SELECTED_SEGMENTS
          ? allSelectedSegmentIds.has(id) // For paginated view, check against all selected IDs
          : selectedSegments.some(seg => seg.id === id) // For group view, check against selectedSegments

      return {
        id,
        label: name,
        checked: isChecked,
        selected: false, // Segments don't need row selection, only checkbox
      }
    })
  }, [segmentList, selectedSegments, type, allSegments, allSelectedSegmentIds])

  const tagsToDisplay = useMemo(() => {
    const newSegmentsList = selectedSegments?.map(item => {
      const match = existingSelections?.segmentsView?.find(x => x?.segmentId === item?.id)

      if (match) {
        return {
          ...item,
          name: match?.segmentName,
        }
      }
      return item
    })
    if (type === SegmentTypes.SELECTED_SEGMENTS) {
      return newSegmentsList
    }

    // For SELECTED_GROUPS combine selections from all groups
    const unique = {}
    Object.values(segmentSelectionsByGroup).forEach(segs => {
      segs.forEach(seg => {
        if (seg?.id) unique[seg.id] = seg
      })
    })
    return Object.values(unique)
  }, [type, selectedSegments, segmentSelectionsByGroup])

  return (
    <div className="flex flex-col">
      {/* Selected segment tags */}
      <div className="mb-4 mt-4 flex flex-row flex-wrap items-center gap-2">
        {tagsToDisplay?.length > 0 &&
          tagsToDisplay.map(seg => (
            <Tag
              key={seg.id}
              id={seg.id}
              text={seg.name}
              isRemovable={true}
              color="blue"
              onRemove={() => handleTagRemove(seg.id)}
            />
          ))}
      </div>
      <div className="flex flex-1 pt-6">
        {type === SegmentTypes.SELECTED_SEGMENTS ? (
          // For SELECTED_SEGMENTS, show only segments list with pagination and API search
          <SelectableList
            key={`segments-page-${currentPage}`}
            title=""
            items={transformedSegments}
            onCheckboxChange={handleSegmentCheckboxChange}
            showSearch={true}
            searchPlaceholder="Search segments..."
            selectable={false} // Segments are checkbox-only, not clickable rows
            showSelectedCount={true}
            selectedCount={allSelectedSegmentIds?.size}
            separateItems={true}
            className="flex-1 px-0"
            // API search props
            useExternalSearch={true}
            onSearchChange={handleApiSearch}
            isLoading={isLoadingSegments}
            // Pagination props
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            totalItems={totalSegments}
          />
        ) : (
          // For SELECTED_GROUPS, show folders and segments without pagination
          // Uses client-side search (default behavior of SelectableList)
          <>
            <SelectableList
              title="Folder"
              items={transformedSegmentGroups}
              onItemClick={handleGroupSelect}
              onCheckboxChange={handleCheckboxChange}
              showSearch={true}
              searchPlaceholder="Search folders..."
              selectable={true}
              showSelectedCount={false}
              separateItems={false}
              className="w-1/2 flex-1 border-r border-[#4E4E4E]"
              // Uses default client-side search (useExternalSearch=false by default)
            />
            {isLoadingSegments ? (
              <div className="flex w-1/2 flex-1 items-center justify-center">
                <Text isDark>Loading segments...</Text>
              </div>
            ) : segmentList?.length > 0 ? (
              <SelectableList
                title="Segments"
                items={transformedSegments}
                onCheckboxChange={handleSegmentCheckboxChange}
                showSearch={true}
                searchPlaceholder="Search segments..."
                selectable={false} // Segments are checkbox-only, not clickable rows
                showSelectedCount={false}
                separateItems={true}
                className="w-1/2 flex-1"
                // Uses default client-side search (useExternalSearch=false by default)
              />
            ) : (
              <div className="flex min-w-[250px] flex-1 items-center justify-center">
                <Text isDark>
                  {!selectedSegmentGroupId ? 'Please select a folder to view segments' : 'No segments found'}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const SegmentModal = ({
  show,
  type,
  onClose,
  onSelect,
  onSubmit,
  segmentGroupData,
  existingSelections,
  isExcluded,
  excludeSegmentIds,
}) => {
  const getModalTitle = () => {
    switch (type) {
      case SegmentTypes.SELECTED_GROUPS:
        return 'Segment'
      case SegmentTypes.SELECTED_SEGMENTS:
        return 'Segments'
      default:
        return ''
    }
  }

  const getModalText = () => {
    switch (type) {
      case SegmentTypes.SELECTED_GROUPS:
        return 'Segment Folder'
      case SegmentTypes.SELECTED_SEGMENTS:
        return 'Segment List'
      default:
        return ''
    }
  }

  return (
    <Modal
      showModal={show}
      isDark
      title={getModalTitle()}
      text={getModalText()}
      primaryButtonDisabled={false}
      primaryButtonText="Apply"
      tertiaryButtonText="Cancel"
      showTertiaryButton={true}
      content={
        <SegmentModalContent
          type={type}
          onSelection={onSelect}
          segmentGroupData={segmentGroupData}
          existingSelections={existingSelections}
          isExcluded={isExcluded}
          excludeSegmentIds={excludeSegmentIds}
        />
      }
      type={MODAL_TYPE.LARGE}
      onClickTertiaryButton={onClose}
      onClickPrimaryButton={onSubmit}
      hideCloseButton={true}
      className="products-promotions-segment-modal"
    />
  )
}

export default SegmentModal
